/**
 * Direct Messages transport (HTTP polling mode).
 *
 * Socket.IO is intentionally disabled for FrontDesk DMs. Call sites keep using
 * this service's connect()/listener APIs, but no socket.io network traffic is
 * created. Live updates come from REST polling in:
 * - InternalChatPopup
 * - InternalConversationPane
 * - useDirectMessages
 * - useUnifiedConversations
 */

import type { Socket } from "socket.io-client";
import config from "@/config";
import {
  DMSendMessageEvent,
  DMMessageConfirmedEvent,
  DMNewMessageEvent,
  DMTypingEvent,
  DMUserTypingEvent,
  DMMarkAsReadEvent,
  DMMessageReadEvent,
  JoinConversationEvent,
  DMPresenceData,
  DMUnreadCountUpdatedEvent,
} from "@/widgets/frontdesk/providers/internal/types/direct-messages.types";

export type DMMessageEventHandler = (message: DMNewMessageEvent) => void;
export type DMMessageConfirmedHandler = (data: DMMessageConfirmedEvent) => void;
export type DMTypingEventHandler = (data: DMUserTypingEvent) => void;
export type DMConnectionEventHandler = (connected: boolean) => void;
export type DMErrorEventHandler = (error: string) => void;
export type DMUnreadCountEventHandler = (data: DMUnreadCountUpdatedEvent) => void;
export type DMConversationCreatedHandler = (data: {
  conversationId: string;
  conversation?: unknown;
  createdBy?: string;
}) => void;

export class DirectMessagesWebSocketService {
  private serverUrl: string;
  private connected = false;
  /** Kept for API compatibility; never fired without a live socket. */
  private durableHandlers: Map<string, Set<(...args: any[]) => void>> = new Map();

  private isDebugEnabled(): boolean {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w: any = typeof window !== "undefined" ? window : {};
      return (
        process.env.NEXT_PUBLIC_DM_DEBUG === "true" || w.DM_DEBUG === true
      );
    } catch {
      return false;
    }
  }

  constructor() {
    this.serverUrl = config.messaging_server || config.api_server;
  }

  connect(_authToken?: string): Promise<Socket> {
    this.connected = true;
    if (this.isDebugEnabled()) {
      console.log(
        `[DM] HTTP polling mode (Socket.IO disabled). Server ref: ${this.serverUrl}`,
      );
    }
    // Stub socket so existing `await connect()` call sites keep working.
    return Promise.resolve({ connected: true } as Socket);
  }

  disconnect(): void {
    this.connected = false;
  }

  isConnected(): boolean {
    return this.connected;
  }

  getSocket(): Socket | null {
    return null;
  }

  joinConversation(_data: JoinConversationEvent): Promise<void> {
    return Promise.resolve();
  }

  leaveConversation(_data: JoinConversationEvent): Promise<void> {
    return Promise.resolve();
  }

  /**
   * @deprecated Use directMessagesService.sendMessage (REST). No-op in polling mode.
   */
  sendMessage(_data: DMSendMessageEvent): void {
    if (this.isDebugEnabled()) {
      console.warn(
        "[DM] sendMessage via Socket.IO is disabled — use REST directMessagesService.sendMessage",
      );
    }
  }

  startTyping(_data: DMTypingEvent): void {
    // Typing is socket-only; ignored in polling mode
  }

  stopTyping(_data: DMTypingEvent): void {
    // Typing is socket-only; ignored in polling mode
  }

  markAsRead(_data: DMMarkAsReadEvent): Promise<void> {
    // Prefer REST markAsRead; resolve so callers do not throw
    return Promise.resolve();
  }

  getPresence(_userIds?: string[]): Promise<Record<string, DMPresenceData>> {
    // Presence was socket-only; return empty map in polling mode
    return Promise.resolve({});
  }

  private onDurable(event: string, handler: (...args: any[]) => void): void {
    if (!this.durableHandlers.has(event)) {
      this.durableHandlers.set(event, new Set());
    }
    this.durableHandlers.get(event)!.add(handler);
  }

  private offDurable(event: string, handler?: (...args: any[]) => void): void {
    const set = this.durableHandlers.get(event);
    if (handler) {
      set?.delete(handler);
    } else {
      this.durableHandlers.delete(event);
    }
  }

  onNewMessage(handler: DMMessageEventHandler): void {
    this.onDurable("dm:newMessage", handler);
  }

  onMessageConfirmed(handler: DMMessageConfirmedHandler): void {
    this.onDurable("dm:messageConfirmed", handler);
  }

  onUserTyping(handler: DMTypingEventHandler): void {
    this.onDurable("dm:userTyping", handler);
  }

  onMessageRead(handler: (data: DMMessageReadEvent) => void): void {
    this.onDurable("dm:messageRead", handler);
  }

  onUnreadCountUpdated(handler: DMUnreadCountEventHandler): void {
    this.onDurable("dm:unreadCountUpdated", handler);
  }

  onConversationUpdated(handler: (data: any) => void): void {
    this.onDurable("dm:conversationUpdated", handler);
  }

  onConversationCreated(handler: DMConversationCreatedHandler): void {
    this.onDurable("dm:conversationCreated", handler);
  }

  onStatusChanged(handler: (data: any) => void): void {
    this.onDurable("presence:update", handler);
  }

  onWelcome(handler: (data: { conversationIds: string[] }) => void): void {
    this.onDurable("dm:welcome", handler);
  }

  onReconnect(handler: (attemptNumber: number) => void): void {
    this.onDurable("reconnect", handler as unknown as (...args: any[]) => void);
  }

  onConnect(handler: DMConnectionEventHandler): void {
    this.onDurable("connect", () => handler(true));
  }

  onDisconnect(handler: DMConnectionEventHandler): void {
    this.onDurable("disconnect", () => handler(false));
  }

  onError(handler: DMErrorEventHandler): void {
    this.onDurable("connect_error", (error: Error) => handler(error.message));
    this.onDurable("exception", (error: any) =>
      handler(error.message || "Socket exception"),
    );
  }

  removeAllListeners(): void {
    this.durableHandlers.clear();
  }

  removeListener(event: string, handler?: (...args: any[]) => void): void {
    this.offDurable(event, handler);
  }
}

export const directMessagesWebSocketService =
  new DirectMessagesWebSocketService();
